import * as Sentry from "@sentry/react";
import { z } from "zod";

// Static envs (managed in `.github/frontend-envs.json` `envs.<env>.frontend`)
// plus the feature-env shape `ft-<numeric-ticket>` that the FeatureEnvStack
// writes at deploy time. Pattern matches the validation in
// `.github/workflows/feature-env-lifecycle.yml` (`^ft-[0-9]+$`); examples:
// `ft-12358`. Consumers that only care about `local` continue to work
// (feature-envs are not local).
const StaticDeployEnvSchema = z.enum([
  "local",
  "dev",
  "stg",
  "partner",
  "prod",
]);
// Brand the feature-env regex so `z.infer<typeof DeployEnvSchema>` stays a
// discriminating union instead of collapsing to plain `string`. Downstream
// callers can still do `deployEnv === "local"` (literal narrows out the
// branded branch); a value like `ft-12358` is typed as
// `string & z.BRAND<"FeatureEnvName">` so it cannot be confused with an
// arbitrary string literal at compile time.
const FeatureEnvNameSchema = z
  .string()
  .regex(/^ft-[0-9]+$/)
  .brand<"FeatureEnvName">();

const DeployEnvSchema = z.union([StaticDeployEnvSchema, FeatureEnvNameSchema]);

export type DeployEnv = z.infer<typeof DeployEnvSchema>;

export const RuntimeConfigSchema = z.object({
  VITE_DEPLOY_ENV: DeployEnvSchema,
  VITE_GRAPHQL_URL: z.string().url(),
  // The frontend's own origin (e.g. "https://www.stg.meistersystems.de"),
  // mirroring `VITE_WIZARD_ORIGIN` in apps/frontend-wizard.
  // `window.location.origin` isn't a usable substitute on native
  // Capacitor builds, where `window.location.hostname` is always
  // "localhost". Optional: the infra deploy stage that writes web's
  // `/config.json` doesn't produce this field yet, so CallAssistant.tsx
  // falls back to `window.location` when it's absent.
  VITE_APP_ORIGIN: z.string().url().optional(),
  VITE_KEYCLOAK_CLIENT: z.string().min(1),
  VITE_THREED_PLANNER_URL: z.string().url(),
  VITE_ICAL_GENERATOR_ENDPOINT: z.string().url(),
  VITE_EMBEDDABLE_WIZARD_URL: z.string().url(),
  // Public client-exposed by design; protected by GCP HTTP referrer
  // restriction on the API key. See MEIS-11514 for the operational
  // tracking ticket of an edge case (PDF generator has no Referer).
  VITE_GOOGLE_MAPS_API_KEY: z.string().min(1),
  // iOS Maps key, restricted by bundle identifier `de.meistersystems` in
  // Google Cloud Console. Required (MEIS-12455): every `/config.json` —
  // web-served, mobile-bundled, and local-dev — MUST carry this value so
  // `loadRuntimeConfig()` validation succeeds at app boot. Only the iOS
  // render path actually uses it (`Capacitor.getPlatform() === "ios"`);
  // exposing it to web/Android clients is operationally safe because
  // the GCP bundle-ID restriction limits use to the iOS bundle.
  VITE_GOOGLE_MAPS_API_KEY_IOS: z.string().min(1),
  // OAuth client id of the Google Identity Services "One Tap" prompt shown
  // on `/signup` (MEIS-13597). Optional: envs without a configured Google
  // OAuth client omit it and `/signup` offers only the "Continue with
  // Google" button. Deliberately no `.min(1)`: an optional enhancement must
  // never be able to fail validation and take the whole app down at boot, so
  // the consumer treats an empty string like an absent key.
  VITE_GOOGLE_ONE_TAP_CLIENT_ID: z.string().optional(),
  // Per-build identity, written by the deploy stage of the CodePipeline
  // (or by `write-mobile-config.ts` for Capacitor mobile builds) to the
  // commit SHA of the deployed Frontend image. Optional during the
  // MEIS-12358 Phase 8 rollout — old infra deploy stages don't yet
  // write the field; callers read it through `getVersion()` in
  // `environment.ts`, which returns `undefined` when absent and lets
  // UI surfaces fall back to "unknown" (the legacy build-time
  // `VERSION` const was removed in Phase 8). Once every env's deploy
  // stage writes `version`, this can be tightened to required.
  version: z.string().min(1).optional(),

  // Published sha-accurately by the deploy stage (MEIS-13220): read from
  // the exported frontend image's `dist/api-compat-version.json` (emitted
  // by the Vite build, see vite.config.ts) and merged into `/config.json`.
  // Optional — feature envs render `/config.json` from a CDK SSM
  // parameter without this field, and local dev has no deploy stage at
  // all. Callers read it through `getApiCompatVersion()` in
  // `environment.ts`, which returns `undefined` when absent and disables
  // the deploy-compatibility UX (design.md D3) rather than failing.
  apiCompatVersion: z.number().int().optional(),
});
// Intentionally NOT `.strict()`: an already-shipped bundle can never be
// taught new keys, so a strict runtime schema turns every future
// config-key addition into a potential outage for a stale cache or rollout
// skew (MEIS-13225). Zod's default object mode strips unknown keys, so
// nothing unknown leaks into `RuntimeConfig`; `loadRuntimeConfig()` below
// detects and loudly reports any unknown keys before parsing. Build-time
// writers use `StrictRuntimeConfigSchema` instead, since a config-source
// typo should fail the build rather than be silently stripped.

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

// Build-time-only derivation: the config writers (`write-local-config.ts`,
// `write-mobile-config.ts`) validate against this instead of
// `RuntimeConfigSchema` so a typo key in the config source fails the build
// loudly. Same field set, same file — not a second schema (D3).
export const StrictRuntimeConfigSchema = RuntimeConfigSchema.strict();

let runtimeConfig: RuntimeConfig | undefined;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// MEIS-13225: `RuntimeConfigSchema` tolerates unknown keys (strips them),
// but silent stripping would hide a future config-key addition landing on
// a stale, already-shipped bundle. Detect and report loudly instead, before
// parsing, so a straggler bundle stays informative even though it keeps
// booting. Guarded on the payload being a plain object — a malformed
// (non-object) payload just falls through to Zod's normal parse failure.
function reportUnknownConfigKeys(payload: unknown): void {
  if (!isPlainObject(payload)) {
    return;
  }
  // Own-property check, not `in`: `in` walks the prototype chain, so a
  // payload key like "toString" or "constructor" would match an inherited
  // Object.prototype member and escape the report while still being
  // stripped by Zod.
  const unknownKeys = Object.keys(payload)
    .filter(
      key =>
        !Object.prototype.hasOwnProperty.call(RuntimeConfigSchema.shape, key)
    )
    .sort();
  if (unknownKeys.length === 0) {
    return;
  }
  const message = `Ignoring unknown /config.json key(s): ${unknownKeys.join(", ")}`;
  console.warn(message);
  Sentry.captureMessage(message, { level: "warning" });
}

// MEIS-13597: feature envs deployed in the infra-driven ("gha") mode get their
// `/config.json` from a CDK SSM parameter that names the shared client
// `meistersystems-feature` for every branch. A branch containing this frontend
// needs `meistersystems-feature-v2` (redesigned login theme and identity-first
// flow), which `.github/frontend-envs.json` already names for the other deploy
// mode. Remove once the infra parameter names the v2 client (MEIS-13611).
const SHARED_FEATURE_KEYCLOAK_CLIENT = "meistersystems-feature";
const REDESIGNED_FEATURE_KEYCLOAK_CLIENT = "meistersystems-feature-v2";

function withRedesignedFeatureClient(config: RuntimeConfig): RuntimeConfig {
  return config.VITE_KEYCLOAK_CLIENT === SHARED_FEATURE_KEYCLOAK_CLIENT
    ? { ...config, VITE_KEYCLOAK_CLIENT: REDESIGNED_FEATURE_KEYCLOAK_CLIENT }
    : config;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // fetch() without a mode option defaults to mode=cors + credentials=same-origin.
  // same-origin credentials send auth cookies the way index.html itself does —
  // auth-protected deployments would otherwise 401. Must match the
  // `<link rel="preload" href="/config.json" as="fetch" crossorigin>` in the
  // entrypoint HTML (`crossorigin` sets mode=cors + credentials=same-origin)
  // so the preloaded response is reused.
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch /config.json: ${response.status} ${response.statusText}`
    );
  }
  const payload: unknown = await response.json();
  reportUnknownConfigKeys(payload);
  runtimeConfig = withRedesignedFeatureClient(
    RuntimeConfigSchema.parse(payload)
  );
  return runtimeConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!runtimeConfig) {
    throw new Error(
      "Runtime config not loaded. loadRuntimeConfig() must complete before getRuntimeConfig() is called. " +
        "If this fires inside a test, call setRuntimeConfigForTesting() in setup."
    );
  }
  return runtimeConfig;
}

export function setRuntimeConfigForTesting(config: RuntimeConfig): void {
  runtimeConfig = config;
}

export function clearRuntimeConfigForTesting(): void {
  runtimeConfig = undefined;
}
